<template>
  <div class="h-full flex flex-col bg-white dark:bg-gray-900 transition-colors duration-300">
    
    <div class="p-4 border-b bg-gray-50 flex-shrink-0 dark:bg-gray-800 dark:border-gray-700 transition-colors">
      
      <div class="flex flex-wrap gap-4 items-center justify-between mb-4">
        <div class="flex flex-wrap gap-4">
           <div class="flex items-center gap-2">
              <strong class="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                Dpto
                <button v-if="editMode" @click="showDeptModal = true" class="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs">⚙️</button>:
              </strong>
              <div class="flex gap-1 flex-wrap">
                <button 
                  v-for="dept in quickFilters.departamentos"
                  :key="dept.value"
                  @click="quickFilter('archivo_departamento', dept.value)"
                  class="px-2 py-1 text-xs font-medium rounded border transition-colors"
                  :class="filters.archivo_departamento === dept.value
                    ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-600 dark:text-white dark:border-blue-500'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'"
                >
                  {{ dept.label }}
                </button>
              </div>
           </div>

           <div class="flex items-center gap-2">
              <strong class="text-sm text-gray-700 dark:text-gray-300">Estado:</strong>
              <div class="flex gap-1">
                <button 
                  v-for="estado in quickFilters.estados"
                  :key="estado.value"
                  @click="quickFilter('estado', estado.value)"
                  class="px-2 py-1 text-xs font-medium rounded border transition-colors flex items-center gap-1"
                  :class="filters.estado === estado.value
                    ? `bg-opacity-20 border-opacity-50 dark:bg-opacity-30 ${estado.class === 'status-aprobado' ? 'bg-green-100 text-green-700 border-green-300 dark:text-green-300 dark:border-green-700' : estado.class === 'status-descartado' ? 'bg-red-100 text-red-700 border-red-300 dark:text-red-300 dark:border-red-700' : 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:text-yellow-300 dark:border-yellow-700'}`
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'"
                >
                  {{ estado.label }}
                </button>
              </div>
           </div>
        </div>
      </div>

      <div class="flex flex-wrap gap-2 items-center">
        
        <div class="relative flex-grow min-w-[200px]">
          <div 
            class="absolute text-gray-400 dark:text-gray-500 pointer-events-none flex items-center"
            style="left: 10px; top: 50%; transform: translateY(-50%); z-index: 10;"
          >
            <SearchIcon :size="16" />
          </div>
          <input 
            type="text" 
            v-model="filters.search" 
            placeholder="Buscar por asunto, remitente..." 
            @input="applyFilters" 
            class="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-gray-900 dark:border-gray-600 dark:text-white dark:placeholder-gray-500 transition-colors"
          />
        </div>

        <DateField 
          v-model="filters.fecha_recepcion" 
          @update:modelValue="applyFilters"
          :inputClass="'px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-gray-900 dark:border-gray-600 dark:text-gray-300 transition-colors'"
        />

        <button 
          @click="resetFilters" 
          class="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
        >
          Limpiar
        </button>

        <button 
          @click="toggleEditMode" 
          class="px-4 py-2 text-sm font-medium border rounded transition-colors ml-auto flex items-center gap-2"
          :class="editMode 
            ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400' 
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'"
        >
          {{ editMode ? 'Salir Edición' : 'Editar' }} ✏️
        </button>
      </div>
    </div>

    <div class="flex-grow overflow-auto p-0 relative bg-white dark:bg-gray-900 custom-scrollbar">
      <table class="w-full border-collapse min-w-[800px]">
        <thead class="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 shadow-sm transition-colors">
          <tr>
            <th 
              v-for="(col, index) in visibleColumns" 
              :key="col.key"
              class="py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 transition-colors"
              :class="{
                'text-center': col.key === 'estado',
                'w-32 px-2': col.key === 'estado',
                'pl-8': index === 0, /* Espacio EXTRA a la izquierda en la primera columna */
                'px-3': index !== 0
              }"
            >
              <div 
                class="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400" 
                :class="{'justify-center': col.key === 'estado'}"
                @click="isSortable(col.key) ? toggleSort(col.key) : null"
              >
                {{ col.label }}
                <span v-if="isSortable(col.key)" class="text-[10px] ml-1">
                    {{ sortKey === col.key ? (sortOrder === 'desc' ? '▼' : '▲') : '⇅' }}
                </span>
              </div>
            </th>

            <th class="px-2 py-3 border-b border-gray-200 dark:border-gray-700 w-10 text-center relative fixed-col-header transition-colors pr-8">
                <button @click.stop="toggleColumnMenu" class="text-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700 rounded px-2 font-bold text-lg leading-none">
                    +
                </button>
                <div v-if="showColumnMenu" class="absolute right-8 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl rounded-lg w-64 z-50 text-left overflow-hidden" @click.stop>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-2 text-xs font-bold text-gray-500 dark:text-gray-300 border-b dark:border-gray-600">CONFIGURAR CAMPOS</div>
                    <div class="max-h-60 overflow-y-auto p-2">
                      <div v-for="(col, index) in columnState" :key="col.key" class="flex items-center justify-between py-1 px-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer flex-grow">
                            <input type="checkbox" v-model="col.visible" @change="saveColumnPreferences" class="rounded text-blue-600 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-600"> 
                            {{col.label}}
                          </label>
                          <div class="flex gap-1 ml-2">
                              <button @click.stop="moveColumn(index, -1)" :disabled="index === 0" class="text-gray-400 hover:text-gray-600 disabled:opacity-30">▲</button>
                              <button @click.stop="moveColumn(index, 1)" :disabled="index === columnState.length - 1" class="text-gray-400 hover:text-gray-600 disabled:opacity-30">▼</button>
                          </div>
                      </div>
                    </div>
                </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900 transition-colors">
           <tr v-if="loading">
             <td :colspan="visibleColumns.length + 1" class="p-8 text-center text-gray-500 dark:text-gray-400">
                <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2 align-middle"></div>
                Cargando datos...
             </td>
           </tr>
           
           <tr v-else-if="files.length === 0">
             <td :colspan="visibleColumns.length + 1" class="p-8 text-center text-gray-500 dark:text-gray-400 italic">
                No se encontraron archivos con los filtros actuales.
             </td>
           </tr>
           
           <tr 
             v-for="file in sortedFiles" 
             :key="file.id" 
             class="hover:bg-blue-50 dark:hover:bg-gray-800 cursor-pointer transition-colors group border-b dark:border-gray-800 h-12"
             @click="abrirDetalle(file)"
           >
              <td 
                v-for="(col, index) in visibleColumns" 
                :key="col.key" 
                class="h-12 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap align-middle"
                :class="{
                  'text-center': col.key === 'estado',
                  'w-32 px-2': col.key === 'estado',
                  'pl-8': index === 0, /* Padding EXTRA a la izquierda */
                  'px-3': index !== 0
                }"
              >
                 <template v-if="col.key === 'estado'">
                    <div class="flex items-center justify-center w-full">
                        <div v-if="file.estado === 'pendiente'" class="flex items-center justify-center">
                            <span class="status-badge status-pendiente">Pendiente</span>
                            <div class="estado-actions flex gap-1 ml-2" v-if="!editMode">
                                <button class="w-6 h-6 rounded bg-green-500 text-white hover:bg-green-600 flex items-center justify-center shadow-sm" @click.stop="actualizarEstado(file, 'aprobado')" title="Aprobar">✓</button>
                                <button class="w-6 h-6 rounded bg-red-500 text-white hover:bg-red-600 flex items-center justify-center shadow-sm" @click.stop="actualizarEstado(file, 'descartado')" title="Descartar">✕</button>
                            </div>
                        </div>
                        <div v-else class="flex items-center justify-center gap-2">
                            <span :class="['status-badge', file.estado === 'aprobado' ? 'status-aprobado' : 'status-descartado']">
                                {{ file.estado === 'aprobado' ? 'Aprobado' : 'Descartado' }}
                            </span>
                            <button v-if="editMode" @click.stop="actualizarEstado(file, 'pendiente')" class="text-gray-400 hover:text-red-500 w-5 h-5 flex items-center justify-center" title="Resetear">✕</button>
                            <span v-else class="inline-block w-5 h-5" aria-hidden="true"></span>
                        </div>
                    </div>
                 </template>

                 <template v-else-if="col.key === 'enlace_digital'">
                    <a v-if="file.enlace_digital" :href="file.enlace_digital" target="_blank" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium hover:underline flex items-center gap-1" @click.stop>
                        📄 Ver
                    </a>
                 </template>

                 <template v-else-if="col.key === 'fecha_recepcion'">
                    {{ formatDate(file[col.key]) }}
                 </template>

                 <template v-else>
                    {{ file[col.key] }}
                 </template>
              </td>
              
              <td class="px-2 h-12 text-center w-10 align-middle pr-8"> 
                  <button v-if="editMode" @click.stop="deleteFile(file)" class="text-red-400 hover:text-red-600 transition-colors inline-flex items-center justify-center w-6 h-6 leading-none" title="Borrar archivo">🗑️</button>
                  <span v-else class="inline-flex items-center justify-center w-6 h-6 leading-none" aria-hidden="true"></span>
              </td>
           </tr>
        </tbody>
      </table>
    </div>

    <ConsultModal 
      v-if="selectedFile" 
      :file="selectedFile" 
      :loading="isUpdatingStatus"
      @close="cerrarDetalle"
      @save="guardarCambiosDesdeModal"
      @update-status="actualizarEstadoDesdeModal"
    />

    <div v-if="showDeptModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="showDeptModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden transition-colors">
            <div class="px-6 py-4 border-b dark:border-gray-700">
                <h2 class="text-lg font-bold text-gray-800 dark:text-white">Gestionar Departamentos</h2>
            </div>
            <div class="p-6">
                <div class="mb-4 max-h-60 overflow-y-auto border dark:border-gray-600 rounded divide-y dark:divide-gray-600">
                    <div v-for="(dept, index) in quickFilters.departamentos" :key="index" class="flex justify-between items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <span class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ dept.label }}</span>
                        <button @click="removeDepartment(index)" class="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded">🗑️</button>
                    </div>
                </div>
                <div class="flex gap-2">
                    <input type="text" v-model="newDeptName" placeholder="Nuevo Departamento..." class="flex-grow border dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white" @keyup.enter="addDepartment">
                    <button @click="addDepartment" class="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50" :disabled="!newDeptName">Añadir</button>
                </div>
            </div>
            <div class="bg-gray-50 dark:bg-gray-700 px-6 py-3 flex justify-end">
                <button class="text-gray-600 dark:text-gray-300 hover:text-gray-800 font-medium text-sm" @click="showDeptModal = false">Cerrar</button>
            </div>
        </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { Search as SearchIcon } from 'lucide-vue-next';
import ConsultModal from './ConsultModal.vue';
import DateField from './DateField.vue';
import { DOCUMENT_TYPES, DOCUMENT_ORIGINS } from '../constants/documentTypes';
import { loadDepartments, saveDepartments } from '../utils/departments';

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';

export default {
  name: 'FileTable',
  components: {
    ConsultModal,
    SearchIcon,
    DateField
  },
  data() {
    return {
      files: [],
      loading: true,
      filters: { search: '', fecha_recepcion: '', archivo_departamento: '', estado: '' },
      quickFilters: {
        // Departamentos: se rellenan en created() desde el helper compartido
        departamentos: [],
        estados: [
          { label: 'Pendientes', value: 'pendiente', class: 'status-pendiente' },
          { label: 'Aprobados', value: 'aprobado', class: 'status-aprobado' },
          { label: 'Descartados', value: 'descartado', class: 'status-descartado' }
        ]
      },
      showColumnMenu: false, showDeptModal: false, newDeptName: '',
      selectedFile: null, isUpdatingStatus: false, editMode: false, 
      columnState: [], sortKey: null, sortOrder: 'asc',
      optionsOrigen: DOCUMENT_ORIGINS,
      optionsTipoDoc: DOCUMENT_TYPES,
    };
  },
  computed: {
    visibleColumns() { return this.columnState.filter(col => col.visible); },
    sortedFiles() {
        if (!this.sortKey) return this.files;
        return [...this.files].sort((a, b) => {
            let valA = a[this.sortKey];
            let valB = b[this.sortKey];
            if (valA === null || valA === undefined) valA = '';
            if (valB === null || valB === undefined) valB = '';
            let result = 0;
            if (typeof valA === 'string' && typeof valB === 'string') result = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
            else { if (valA < valB) result = -1; if (valA > valB) result = 1; }
            return this.sortOrder === 'desc' ? result * -1 : result;
        });
    }
  },
  async created() {
    window.addEventListener('click', this.closeColumnMenu);
    this.quickFilters.departamentos = loadDepartments();
    await this.fetchColumns();
    await this.fetchFiles();
  },
  beforeUnmount() { window.removeEventListener('click', this.closeColumnMenu); },
  methods: {
    // Llamado por el padre si es necesario forzar refresco
    refresh() { this.fetchFiles(); },

    async fetchFiles() {
      this.loading = true;
      try {
        // Combinamos filtros recibidos por prop
        const response = await axios.get(`${API_BASE_URL}/files/`, { params: this.filters });
        this.files = response.data.results || response.data;
      } catch (e) { 
        if (e.response && e.response.status === 401) return; 
        console.error("Error fetching files:", e); 
      } finally { 
        this.loading = false; 
      }
    },

    // --- LÓGICA DE COLUMNAS ---
    async fetchColumns() {
        try {
            const response = await axios.get(`${API_BASE_URL}/files/columns/`);
            const backendColumns = response.data;
            const savedStateStr = localStorage.getItem('promofiles_column_state');
            
            if (savedStateStr) {
                try {
                    const savedState = JSON.parse(savedStateStr);
                    const backendMap = new Map(backendColumns.map(col => [col.key, col]));
                    const mergedState = [];
                    // Preservar orden y visibilidad guardada
                    savedState.forEach(savedCol => { 
                        if (backendMap.has(savedCol.key)) { 
                            const backendCol = backendMap.get(savedCol.key); 
                            mergedState.push({ key: savedCol.key, label: backendCol.label, visible: savedCol.visible }); 
                            backendMap.delete(savedCol.key); 
                        } 
                    });
                    // Añadir columnas nuevas que vengan del backend
                    backendMap.forEach(col => { mergedState.push({ key: col.key, label: col.label, visible: col.default }); });
                    this.columnState = mergedState;
                } catch (e) { this.initDefaultColumns(backendColumns); }
            } else { this.initDefaultColumns(backendColumns); }
        } catch (e) { 
            // Fallback si falla el endpoint de columnas
            this.columnState = [ { key: 'asunto', label: 'Asunto', visible: true }, { key: 'remitente', label: 'Remitente', visible: true }, { key: 'fecha_recepcion', label: 'Fecha', visible: true }, { key: 'estado', label: 'Estado', visible: true }, { key: 'enlace_digital', label: 'Enlace', visible: true } ]; 
        }
    },
    initDefaultColumns(backendColumns) { this.columnState = backendColumns.map(col => ({ key: col.key, label: col.label, visible: col.default })); },
    toggleColumnMenu() { this.showColumnMenu = !this.showColumnMenu; },
    closeColumnMenu(e) { if (!e.target.closest('.fixed-col-header')) this.showColumnMenu = false; },
    saveColumnPreferences() { localStorage.setItem('promofiles_column_state', JSON.stringify(this.columnState)); },
    moveColumn(index, direction) { const newIndex = index + direction; if (newIndex < 0 || newIndex >= this.columnState.length) return; const item = this.columnState[index]; this.columnState.splice(index, 1); this.columnState.splice(newIndex, 0, item); this.saveColumnPreferences(); },
    
    // --- LÓGICA DE ORDENACIÓN ---
    isSortable(key) { return key !== 'enlace_digital'; },
    toggleSort(key) { if (this.sortKey === key) this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'; else { this.sortKey = key; this.sortOrder = 'asc'; } },
    
    // --- LÓGICA DE DETALLE / MODAL ---
    abrirDetalle(file) { 
      // Clonamos el objeto para evitar mutación directa hasta guardar
      this.selectedFile = JSON.parse(JSON.stringify(file)); 
    },
    cerrarDetalle() { this.selectedFile = null; },
    
    // Evento @save del ConsultModal
    async guardarCambiosDesdeModal(fileData) {
        this.isUpdatingStatus = true;
        const url = `${API_BASE_URL}/files/${fileData.id}/`;
        
        // Limpiamos campos de solo lectura antes de enviar
        const payload = { ...fileData };
        delete payload.file_url; 
        delete payload.enlace_digital; 

        try {
            const response = await axios.patch(url, payload);
            
            // Actualizamos la lista local
            const index = this.files.findIndex(f => f.id === response.data.id);
            if (index !== -1) Object.assign(this.files[index], response.data);
            
            this.cerrarDetalle();
        } catch (error) {
            console.error(error);
            alert("Hubo un error al guardar los cambios.");
        } finally {
            this.isUpdatingStatus = false;
        }
    },

    // Evento @update-status del ConsultModal
    async actualizarEstadoDesdeModal({ file, status }) {
        await this.actualizarEstado(file, status);
        // Si queremos cerrar al aprobar/descartar, descomenta la siguiente línea:
        // this.cerrarDetalle();
        // Si no cerramos, actualizamos el selectedFile para que el modal refleje el cambio visualmente
        if (this.selectedFile && this.selectedFile.id === file.id) {
            this.selectedFile.estado = status;
        }
    },

    // Actualización directa (desde la tabla o invocada por el modal)
    async actualizarEstado(file, nuevoEstado) {
        if (this.isUpdatingStatus) return;
        this.isUpdatingStatus = true;
        const url = `${API_BASE_URL}/files/${file.id}/`;
        try {
            const response = await axios.patch(url, { estado: nuevoEstado });
            // Actualizar lista
            const index = this.files.findIndex(f => f.id === file.id);
            if (index !== -1) this.files[index].estado = response.data.estado;
        } catch (error) {
            alert("Hubo un error al actualizar estado.");
        } finally {
            this.isUpdatingStatus = false;
        }
    },

    // --- ACCIONES DE EDICIÓN ---
    toggleEditMode() { this.editMode = !this.editMode; },
    async deleteFile(file) { 
        if (!this.editMode) return; 
        if (confirm(`¿Borrar "${file.asunto}"? Esta acción es irreversible.`)) { 
            const url = `${API_BASE_URL}/files/${file.id}/`; 
            try { 
                await axios.delete(url); 
                this.files = this.files.filter(f => f.id !== file.id); 
            } catch (error) { 
                alert("Error al borrar el archivo."); 
            } 
        } 
    },

    // --- MÉTODOS DE FILTRADO Y BÚSQUEDA ---
    applyFilters() { this.fetchFiles(); },
    resetFilters() { this.filters = { search: '', fecha_recepcion: '', archivo_departamento: '', estado: '' }; this.fetchFiles(); },
    quickFilter(tipo, valor) { this.filters[tipo] = this.filters[tipo] === valor ? '' : valor; this.applyFilters(); },
    addDepartment() { if(!this.newDeptName) return; if(this.quickFilters.departamentos.some(d => d.value === this.newDeptName)) { alert("Este departamento ya existe"); return; } this.quickFilters.departamentos.push({ label: this.newDeptName, value: this.newDeptName }); this.newDeptName = ''; this.saveDepartments(); },
    removeDepartment(index) { if(confirm("¿Seguro que quieres borrar este departamento?")) { this.quickFilters.departamentos.splice(index, 1); this.saveDepartments(); } },
    saveDepartments() { saveDepartments(this.quickFilters.departamentos); },
    formatDate(dateStr) { 
        if (!dateStr) return '';
        const date = new Date(dateStr + 'T00:00:00');
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}/${month}/${day}`;
    }
  }
}

</script>

<style scoped>
.status-badge { display: inline-block; padding: 0.25rem 0.6rem; font-size: 0.75rem; font-weight: 600; border-radius: 9999px; line-height: 1; }
.status-aprobado { background-color: #d1fae5; color: #065f46; }
.status-descartado { background-color: #fee2e2; color: #991b1b; }
.status-pendiente { background-color: #fef3c7; color: #92400e; }

/* Mostrar acciones al pasar el ratón por la fila o la celda de estado */
.estado-actions { opacity: 0; transition: opacity 0.15s ease; }
tr:hover .estado-actions,
td:hover .estado-actions { opacity: 1; }

/* Scrollbar personalizado para el contenedor de la tabla */
.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #475569; }
</style>